import {
  Code2,
  Layers,
  Sparkles,
  ShieldCheck,
  GitBranch,
  Rocket,
  Wrench,
  MonitorSmartphone,
} from "lucide-react";

/**
 * Visual categories for articles.
 *
 * Articles keep their free-form `tags` (the /articles filter still uses them).
 * A category is derived from those tags instead of being stored on each entry,
 * so adding an article only means adding it to articles.js.
 *
 * `rgb` mirrors `color` for rgba() gradients, which have wider browser support
 * than color-mix().
 */
export const CATEGORIES = {
  swift: {
    id: "swift",
    label: "Swift & SwiftUI",
    color: "#FB923C",
    rgb: "251, 146, 60",
    icon: Code2,
    tags: ["Swift 6", "Swift", "SwiftUI", "Concurrency", "Performance", "State Management", "Xcode", "Swift Testing", "Testing"],
  },
  architecture: {
    id: "architecture",
    label: "Architecture",
    color: "#818CF8",
    rgb: "129, 140, 248",
    icon: Layers,
    tags: [
      "Architecture",
      "VIPER",
      "Modular",
      "Clean Code",
      "Design Patterns",
      "Analytics",
      "Deeplink",
      "Routing",
      "Backend",
      "Stitch",
      "Data Integration",
      "Push Notifications",
      "SwiftData",
      "Persistence",
    ],
  },
  ai: {
    id: "ai",
    label: "AI & Machine Learning",
    color: "#C084FC",
    rgb: "192, 132, 252",
    icon: Sparkles,
    tags: [
      "AI",
      "LLM",
      "Core ML",
      "Machine Learning",
      "Apple Intelligence",
      "On-Device AI",
      "AI Tooling",
      "MCP",
      "Foundation Models",
      "Philosophy",
    ],
  },
  security: {
    id: "security",
    label: "Security",
    color: "#34D399",
    rgb: "52, 211, 153",
    icon: ShieldCheck,
    tags: ["Security", "AppSec", "Jailbreak", "Biometrics", "DevSecOps", "Fintech"],
  },
  devops: {
    id: "devops",
    label: "DevOps & CI/CD",
    color: "#22D3EE",
    rgb: "34, 211, 238",
    icon: GitBranch,
    tags: ["CI/CD", "DevOps", "Jenkins", "Fastlane", "Automation", "Tuist", "Xcode Cloud"],
  },
  product: {
    id: "product",
    label: "Product & Design",
    color: "#F472B6",
    rgb: "244, 114, 182",
    icon: Rocket,
    tags: ["Indie Dev", "Product", "Maker", "Strategy", "Agile", "ASO", "Marketing", "UI/UX", "Figma", "Design System", "Accessibility", "StoreKit"],
  },
  tooling: {
    id: "tooling",
    label: "Tooling & Craft",
    color: "#FBBF24",
    rgb: "251, 191, 36",
    icon: Wrench,
    tags: [
      "Productivity",
      "Tooling",
      "Code Quality",
      "Engineering Culture",
      "Documentation",
      "Leadership",
      "Cursor",
    ],
  },
  ecosystem: {
    id: "ecosystem",
    label: "Apple Ecosystem",
    color: "#38BDF8",
    rgb: "56, 189, 248",
    icon: MonitorSmartphone,
    tags: ["macOS", "iOS", "Ecosystem", "Apple", "DX", "App Intents"],
  },
};

export const DEFAULT_CATEGORY = CATEGORIES.architecture;

// tag -> category, built once.
const TAG_INDEX = new Map();
Object.values(CATEGORIES).forEach((category) => {
  category.tags.forEach((tag) => {
    if (!TAG_INDEX.has(tag)) TAG_INDEX.set(tag, category);
  });
});

/**
 * Walks the article's tags in their authored order and returns the first one
 * that a category claims, so the tag the author put first wins.
 */
export const getCategory = (article) => {
  const tags = article?.tags || [];
  for (const tag of tags) {
    const hit = TAG_INDEX.get(tag);
    if (hit) return hit;
  }
  return DEFAULT_CATEGORY;
};
